'use client';

import { memo, useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { ScanLine } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { LeasingPropertyDTO } from '#types/types';
import { INVENTORY_STATS_GLOBAL_SCOPE_KEY, TableStatsResponseV1 } from '#types/types';
import { isoNumericToCountryCode, toIsoCountryCodes } from '#lib/country-codes';

interface PortfolioAssetsCountryMapProps {
  propertyIds?: string[];
  selectedCountryCodes?: string[];
  onToggleCountry?: (countryCode: string) => void;
  disabled?: boolean;
  heightClassName?: string;
}

interface ProjectedPoint {
  x: number;
  y: number;
  original: LeasingPropertyDTO;
}

const WORLD_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

const DOT_GRID_STYLE: CSSProperties = {
  backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
  backgroundSize: '30px 30px',
};

const translations = {
  en: {
    assetsLabel: 'ASSETS',
    selectTargetCountry: 'Click a country to filter',
    zoomLabel: (value: string) => `ZOOM: ${value}x`,
    brandsLabel: 'brands',
    selectedLabel: 'selected',
    unselectedLabel: 'not selected',
    disabledLabel: 'read-only',
  },
  fr: {
    assetsLabel: 'ACTIFS',
    selectTargetCountry: 'Cliquez un pays pour filtrer',
    zoomLabel: (value: string) => `ZOOM: ${value}x`,
    brandsLabel: 'marques',
    selectedLabel: 'selectionne',
    unselectedLabel: 'non selectionne',
    disabledLabel: 'lecture seule',
  },
};

const resolvePropertyCountryCode = (property: LeasingPropertyDTO): string | null => {
  const candidates: unknown[] = [
    property.country,
    property.location?.countryCode,
    property.location?.country,
  ];
  for (const candidate of candidates) {
    const code = toIsoCountryCodes(candidate).at(0);
    if (code) return code;
  }
  return null;
};

const resolveFeatureCountryCode = isoNumericToCountryCode;

const parseBrandCountryCounts = (statsPayload: TableStatsResponseV1 | null | undefined): Map<string, number> => {
  const counts = new Map<string, number>();
  const sections = Array.isArray(statsPayload?.sections) ? statsPayload.sections : [];
  const section = sections.find((entry) => entry.id === 'country_summary');
  if (!section || !Array.isArray(section.rows)) return counts;

  section.rows.forEach((row) => {
    const countryCode = typeof row.key === 'string' ? row.key.trim().toUpperCase() : '';
    if (!countryCode) return;

    const label = typeof row.label === 'string' ? row.label : '';
    const match = label.match(/\(([\d\s,.'’]+)\s+brands\)/i);
    if (match?.[1]) {
      const parsed = Number(match[1].replace(/[^\d]/g, ''));
      if (Number.isFinite(parsed)) {
        counts.set(countryCode, parsed);
        return;
      }
    }

    if (typeof row.value === 'number' && Number.isFinite(row.value)) {
      counts.set(countryCode, Math.max(0, Math.round(row.value)));
    }
  });

  return counts;
};

const PortfolioAssetsCountryMap = memo(({
  propertyIds = [],
  selectedCountryCodes = [],
  onToggleCountry,
  disabled = false,
  heightClassName = 'h-[280px] min-h-[280px]',
}: PortfolioAssetsCountryMapProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapGroupRef = useRef<SVGGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const topologyRef = useRef<any>(null);
  const countryPathsRef = useRef<d3.Selection<SVGPathElement, any, SVGGElement, unknown> | null>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<Element, unknown> | null>(null);
  const currentTransformRef = useRef(d3.zoomIdentity);

  const projectionRef = useRef(d3.geoMercator().scale(150).translate([0, 0]));
  const pathGeneratorRef = useRef(d3.geoPath().projection(projectionRef.current));

  const resolvedProperties = useKernelStore(
    (state) => propertyIds.map((id) => state.inventory.Property.registry[id]).filter(Boolean) as LeasingPropertyDTO[]
  );
  const brandStatsEntry = useKernelStore(
    (state) => state.inventoryStats.Brand[INVENTORY_STATS_GLOBAL_SCOPE_KEY] ?? null,
  );
  const brandCountByCountry = useMemo(
    () => parseBrandCountryCounts(brandStatsEntry?.data),
    [brandStatsEntry?.data],
  );

  const selectedCountrySet = useMemo(
    () => new Set(selectedCountryCodes.map((code) => code.trim().toUpperCase()).filter(Boolean)),
    [selectedCountryCodes],
  );

  const propertiesCountByCountry = useMemo(() => {
    const map = new Map<string, number>();
    resolvedProperties.forEach((property) => {
      const code = resolvePropertyCountryCode(property);
      if (!code) return;
      map.set(code, (map.get(code) || 0) + 1);
    });
    return map;
  }, [resolvedProperties]);

  const projectedPoints = useMemo(() => {
    const points: ProjectedPoint[] = [];
    resolvedProperties.forEach((property) => {
      const lat = property.location?.lat;
      const lng = property.location?.lng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const projection = projectionRef.current([lng!, lat!]);
      if (!projection) return;
      points.push({ x: projection[0], y: projection[1], original: property });
    });
    return points;
  }, [resolvedProperties]);

  const propsRef = useRef({
    projectedPoints,
    selectedCountrySet,
    onToggleCountry,
    disabled,
    propertiesCountByCountry,
    brandCountByCountry,
    t,
  });

  useEffect(() => {
    propsRef.current = {
      projectedPoints,
      selectedCountrySet,
      onToggleCountry,
      disabled,
      propertiesCountByCountry,
      brandCountByCountry,
      t,
    };
  }, [projectedPoints, selectedCountrySet, onToggleCountry, disabled, propertiesCountByCountry, brandCountByCountry, t]);

  useEffect(() => {
    if (!kernel.current) return;
    if (!propertyIds.length) return;
    kernel.current.ensureInventoryIds('Property', propertyIds);
  }, [kernel, propertyIds]);

  useEffect(() => {
    if (!kernel.current) return;
    const shouldLoad = !brandStatsEntry?.data && !brandStatsEntry?.isLoading;
    const shouldRefresh = Boolean(brandStatsEntry?.stale && !brandStatsEntry?.isLoading);
    if (!shouldLoad && !shouldRefresh) return;

    void kernel.current
      .fetchInventoryStats('Brand', {
        force: shouldRefresh,
        // Brand stats endpoint is global-only and rejects organisationId.
        includeOrganisationIdParam: false,
      })
      .catch(() => undefined);
  }, [brandStatsEntry?.data, brandStatsEntry?.isLoading, brandStatsEntry?.stale, kernel]);

  const getCountryFill = useCallback((countryCode: string | null, isHover = false): string => {
    if (!countryCode) return '#1a1a1a';
    const isSelected = propsRef.current.selectedCountrySet.has(countryCode);
    if (isHover && !propsRef.current.disabled) {
      return isSelected ? '#115e59' : '#2a2a2a';
    }
    return isSelected ? '#134e4a' : '#1a1a1a';
  }, []);

  const drawCanvasDots = useCallback((points: ProjectedPoint[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const transform = currentTransformRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const baseRadius = Math.max(1.5, Math.min(window.innerWidth, window.innerHeight) * 0.0018);
    points.forEach((point) => {
      const x = transform.applyX(point.x);
      const y = transform.applyY(point.y);
      if (x < -10 || y < -10 || x > canvas.width + 10 || y > canvas.height + 10) return;

      const countryCode = resolvePropertyCountryCode(point.original);
      const isInSelectedCountry =
        !propsRef.current.selectedCountrySet.size ||
        (countryCode ? propsRef.current.selectedCountrySet.has(countryCode) : false);

      ctx.beginPath();
      ctx.globalAlpha = isInSelectedCountry ? 0.95 : 0.35;
      ctx.fillStyle = isInSelectedCountry ? '#ffffff' : '#8a8f98';
      ctx.arc(x, y, baseRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  }, []);

  const updateViewAndDots = useCallback(() => {
    if (!containerRef.current || !svgRef.current) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const transform = currentTransformRef.current;
    const viewWidth = width / transform.k;
    const viewHeight = height / transform.k;
    const viewX = -transform.x / transform.k;
    const viewY = -transform.y / transform.k;
    d3.select(svgRef.current).attr('viewBox', `${viewX} ${viewY} ${viewWidth} ${viewHeight}`);
    drawCanvasDots(propsRef.current.projectedPoints);
  }, [drawCanvasDots]);

  const updateCountryFills = useCallback(() => {
    if (!countryPathsRef.current) return;
    countryPathsRef.current.attr('fill', (d: any) => getCountryFill(resolveFeatureCountryCode(d.id)));
  }, [getCountryFill]);

  const fitToPointsOrWorld = useCallback(() => {
    if (!containerRef.current || !zoomBehaviorRef.current) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    const points = propsRef.current.projectedPoints;
    if (!points.length) {
      const nextTransform = d3.zoomIdentity.translate(width / 2, height / 2).scale(1);
      d3.select(containerRef.current).call(zoomBehaviorRef.current.transform as any, nextTransform);
      return;
    }

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const y0 = Math.min(...ys);
    const y1 = Math.max(...ys);
    const dx = Math.max(1, x1 - x0);
    const dy = Math.max(1, y1 - y0);
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    // Keep a conservative fit zoom to avoid the "thin horizontal strip" effect
    // when all assets are clustered in a narrow latitude band.
    const scale = Math.max(1, Math.min(6, 0.82 / Math.max(dx / width, dy / height)));
    const nextTransform = d3.zoomIdentity
      .translate(width / 2 - scale * cx, height / 2 - scale * cy)
      .scale(scale);
    d3.select(containerRef.current).call(zoomBehaviorRef.current.transform as any, nextTransform);
  }, []);

  const renderMapStaticLayers = useCallback(() => {
    if (!mapGroupRef.current || !topologyRef.current) return;

    const group = d3.select(mapGroupRef.current);
    group.selectAll('*').remove();

    const paths = group
      .selectAll('path.country')
      .data(topologyRef.current.features)
      .enter()
      .append('path')
      .attr('class', 'country')
      .attr('d', pathGeneratorRef.current as any)
      .attr('stroke', '#333')
      .attr('stroke-width', 0.5)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('fill', (d: any) => getCountryFill(resolveFeatureCountryCode(d.id)))
      .on('mouseover', function (event, d: any) {
        const code = resolveFeatureCountryCode(d.id);
        d3.select(this).attr('fill', getCountryFill(code, true));
        if (!tooltipRef.current || !code) return;
        const count = propsRef.current.propertiesCountByCountry.get(code) || 0;
        const brandCount = propsRef.current.brandCountByCountry.get(code) || 0;
        const isSelected = propsRef.current.selectedCountrySet.has(code);
        tooltipRef.current.innerHTML = `
          <div class="font-bold text-xs mb-1">${code}</div>
          <div class="text-[10px] text-white/70">${count} asset(s)</div>
          <div class="text-[10px] text-white/70">${brandCount} ${propsRef.current.t.brandsLabel}</div>
          <div class="text-[10px] ${isSelected ? 'text-emerald-400' : 'text-white/40'} mt-1">${isSelected ? propsRef.current.t.selectedLabel : propsRef.current.t.unselectedLabel}</div>
        `;
        tooltipRef.current.style.opacity = '1';
      })
      .on('mousemove', function (event) {
        if (!tooltipRef.current || !containerRef.current) return;
        const [x, y] = d3.pointer(event, containerRef.current);
        tooltipRef.current.style.left = `${x}px`;
        tooltipRef.current.style.top = `${y - 10}px`;
        tooltipRef.current.style.transform = 'translate(-50%, -100%)';
      })
      .on('mouseout', function (event, d: any) {
        d3.select(this).attr('fill', getCountryFill(resolveFeatureCountryCode(d.id)));
        if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
      })
      .on('click', function (_event, d: any) {
        if (propsRef.current.disabled) return;
        const code = resolveFeatureCountryCode(d.id);
        if (!code || !propsRef.current.onToggleCountry) return;
        propsRef.current.onToggleCountry(code);
      });

    countryPathsRef.current = paths as any;
  }, [getCountryFill]);

  useEffect(() => {
    if (!containerRef.current || !svgRef.current || !canvasRef.current) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    currentTransformRef.current = d3.zoomIdentity.translate(width / 2, height / 2).scale(1);

    zoomBehaviorRef.current = d3
      .zoom<Element, unknown>()
      .scaleExtent([1, 50])
      .on('zoom', (event) => {
        currentTransformRef.current = event.transform;
        updateViewAndDots();
        d3.select('#portfolio-country-map-zoom').text(
          propsRef.current.t.zoomLabel(event.transform.k.toFixed(1)),
        );
      });

    d3.select(containerRef.current)
      .call(zoomBehaviorRef.current as any)
      .call(zoomBehaviorRef.current.transform as any, currentTransformRef.current);

    const onResize = () => {
      if (!containerRef.current || !canvasRef.current) return;
      canvasRef.current.width = containerRef.current.clientWidth;
      canvasRef.current.height = containerRef.current.clientHeight;
      updateViewAndDots();
    };

    onResize();
    const observer = new ResizeObserver(onResize);
    observer.observe(containerRef.current);

    fetch(WORLD_ATLAS_URL)
      .then((res) => res.json())
      .then((data) => {
        const countries = topojson.feature(data, data.objects.countries);
        topologyRef.current = { features: (countries as any).features };
        renderMapStaticLayers();
        fitToPointsOrWorld();
      });

    return () => {
      observer.disconnect();
    };
  }, [fitToPointsOrWorld, renderMapStaticLayers, updateViewAndDots]);

  useEffect(() => {
    updateCountryFills();
    drawCanvasDots(projectedPoints);
  }, [drawCanvasDots, projectedPoints, selectedCountrySet, updateCountryFills]);

  useEffect(() => {
    if (!topologyRef.current) return;
    // Re-render to refresh tooltip data bindings when brand country stats change.
    renderMapStaticLayers();
  }, [brandCountByCountry, renderMapStaticLayers]);

  useEffect(() => {
    if (!zoomBehaviorRef.current || !containerRef.current) return;
    fitToPointsOrWorld();
  }, [projectedPoints, fitToPointsOrWorld]);

  return (
    <div ref={containerRef} className={`relative w-full overflow-hidden rounded-md border border-[#2A2A2A] bg-[#101010] ${heightClassName}`}>
      <div
        className="pointer-events-none absolute inset-0 opacity-10"
        style={DOT_GRID_STYLE}
      />
      <svg ref={svgRef} width="100%" height="100%" className="absolute inset-0" shapeRendering="geometricPrecision">
        <g ref={mapGroupRef} />
      </svg>
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0" />
      <div
        ref={tooltipRef}
        className="pointer-events-none absolute z-50 rounded border border-white/10 bg-black/90 px-3 py-2 text-white opacity-0 shadow-xl transition-opacity duration-150"
      />

      <div className="pointer-events-none absolute left-3 top-3 flex flex-col gap-0.5">
        <div className="font-mono text-2xl font-bold leading-none text-white">
          {projectedPoints.length} <span className="text-sm font-normal text-white/35">{t.assetsLabel}</span>
        </div>
      </div>

      <div className="pointer-events-none absolute right-3 top-3">
        <div className="flex items-center gap-2 rounded border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] text-white/75 backdrop-blur">
          <ScanLine size={12} className={disabled ? '' : 'animate-pulse'} />
          <span className="font-medium">
            {disabled ? t.disabledLabel : t.selectTargetCountry}
          </span>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-3 left-3 text-[9px] font-mono text-white/25" id="portfolio-country-map-zoom">
        {t.zoomLabel('1.0')}
      </div>
    </div>
  );
});

PortfolioAssetsCountryMap.displayName = 'PortfolioAssetsCountryMap';
export default PortfolioAssetsCountryMap;
